import React from "react";
import Swal from "sweetalert2";

const IndividualisedRehabPlanForm = ({ formData, setFormData }) => {
  // --------------------------------------------------------------
  // Helper: generic add / remove for arrays
  // --------------------------------------------------------------
  const addItem = (key, emptyObj) => {
    setFormData((prev) => ({
      ...prev,
      [key]: [...prev[key], emptyObj],
    }));
  };

  const removeItem = (key, index) => {
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  // --------------------------------------------------------------
  // Objective helpers
  // --------------------------------------------------------------
  const addObjective = () => {
  addItem("objectives", {
    name: "",
    obj_status: "Pending",
    obj_date: new Date().toISOString().split("T")[0],
    actions: [
      {
        action_name: "",
        location: "",
        duration: "",
        frequency: "",
      },
    ],
  });
};

  const removeObjective = (idx) => {
    const objective = formData.objectives[idx];
    if (objective.actions.length) {
      Swal.fire({
        title: "Delete Objective?",
        text: "All actions inside will be removed.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
      }).then((res) => {
        if (res.isConfirmed) removeItem("objectives", idx);
      });
    } else {
      removeItem("objectives", idx);
    }
  };

  const addAction = (objIdx) => {
    const newAction = {
      action_name: "",
      location: "",
      duration: "",
      frequency: "",
    };
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.map((o, i) =>
        i === objIdx ? { ...o, actions: [...o.actions, newAction] } : o
      ),
    }));
  };

  const removeAction = (objIdx, actIdx) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.map((o, i) =>
        i === objIdx
          ? { ...o, actions: o.actions.filter((_, j) => j !== actIdx) }
          : o
      ),
    }));
  };

  // --------------------------------------------------------------
  // Change handlers (generic)
  // --------------------------------------------------------------
  const updateSimple = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const updateArrayItem = (arrayKey, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [arrayKey]: prev[arrayKey].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const updateObjective = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.map((o, i) =>
        i === idx ? { ...o, [field]: value } : o
      ),
    }));
  };

  const updateAction = (objIdx, actIdx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.map((o, i) =>
        i === objIdx
          ? {
            ...o,
            actions: o.actions.map((a, j) =>
              j === actIdx ? { ...a, [field]: value } : a
            ),
          }
          : o
      ),
    }));
  };

  // --------------------------------------------------------------
  // Render
  // --------------------------------------------------------------
  return (
    <div className="p-4">
      {/* ==== Title ==== */}
      <div className="col-12 mb-4">
        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
          Individualised Rehabilitation Plan
        </small>
      </div>

      {/* ==== Domain ==== */}
      <div className="text-center mb-4">
        {["Living", "Learning", "Working", "Socializing"].map((d) => (
          <div className="form-check form-check-inline" key={d}>
            <input
              className="form-check-input"
              type="radio"
              name="domain"
              value={d}
              checked={formData.domain === d}
              onChange={(e) => updateSimple("domain", e.target.value)}
            />
            <label className="form-check-label">{d}</label>
          </div>
        ))}
      </div>

      {/* ==== Strengths & Weaknesses ==== */}
      <div className="row g-3 mb-4">
        {/* Strengths */}
        <div className="col-md-6">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
            Strengths
          </small>
          {formData.strengths.map((s, i) => (
            <div
              key={i}
              className="row g-2 align-items-center mb-2 border rounded-3 p-2 bg-light-subtle"
            >
              <div className="col-md-1 text-center fw-semibold">
                {i + 1}.
              </div>
              <div className="col-md-10">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Strength"
                  value={s.value}
                  onChange={(e) =>
                    updateArrayItem("strengths", i, "value", e.target.value)
                  }
                />
              </div>
              <div className="col-md-1 text-end">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeItem("strengths", i)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm mt-2 w-100"
            onClick={() => addItem("strengths", { value: "" })}
          >
            <i className="ri-add-line"></i> Add Strength
          </button>
        </div>

        {/* w e a k n e s s e s */}
        <div className="col-md-6">
          <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
            Weaknesses
          </small>
          {formData.weaknesses.map((w, i) => (
            <div
              key={i}
              className="row g-2 align-items-center mb-2 border rounded-3 p-2 bg-light-subtle"
            >
              <div className="col-md-1 text-center fw-semibold">
                {i + 1}.
              </div>
              <div className="col-md-10">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Weakness"
                  value={w.value}
                  onChange={(e) =>
                    updateArrayItem("weaknesses", i, "value", e.target.value)
                  }
                />
              </div>
              <div className="col-md-1 text-end">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeItem("weaknesses", i)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm mt-2 w-100"
            onClick={() => addItem("weaknesses", { value: "" })}
          >
            <i className="ri-add-line"></i> Add Weakness
          </button>
        </div>
      </div>

      {/* ==== Objectives & Action Plan ==== */}
      <div className="col-md-12 mb-4">
        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
          Objectives & Action Plan
        </small>

        <div className="accordion" id="objectiveAccordion">
          {formData.objectives.map((obj, oIdx) => (
            <div className="accordion-item mb-2 shadow-sm" key={oIdx}>
              <h2 className="accordion-header d-flex align-items-center justify-content-between">
                <button
                  className="accordion-button collapsed fw-semibold"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${oIdx}`}
                >
                  {oIdx + 1}. {obj.name || "Untitled Objective"}
                  <span className="badge bg-label-info ms-3">
                    {obj.obj_status}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn btn-lg btn-icon btn-outline-danger me-3"
                  onClick={() => removeObjective(oIdx)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </h2>

              <div
                id={`collapse-${oIdx}`}
                className="accordion-collapse collapse"
              >
                <div className="accordion-body">
                  {/* Objective row */}
                  <div className="row g-2 align-items-center mb-3 border rounded-3 p-3 bg-primary-subtle">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Objective Name"
                        value={obj.name}
                        onChange={(e) =>
                          updateObjective(oIdx, "name", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={obj.obj_status}
                        onChange={(e) =>
                          updateObjective(oIdx, "obj_status", e.target.value)
                        }
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        type="date"
                        className="form-control"
                        value={obj.obj_date}
                        onChange={(e) =>
                          updateObjective(oIdx, "obj_date", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="overflow-auto" style={{ maxWidth: "100%" }}>
                    {obj.actions.map((act, aIdx) => (
                      <div
                        key={aIdx}
                        className="row g-2 align-items-center mb-2 flex-nowrap"
                      >
                        <div className="col-md-1">
                          <span className="fw-semibold">
                            {aIdx + 1}.
                          </span>
                        </div>
                        <div className="col-md-4">
                          <input
                            className="form-control"
                            placeholder="Action Name"
                            value={act.action_name}
                            onChange={(e) =>
                              updateAction(
                                oIdx,
                                aIdx,
                                "action_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-md-3">
                          <input
                            className="form-control"
                            placeholder="Location"
                            value={act.location}
                            onChange={(e) =>
                              updateAction(
                                oIdx,
                                aIdx,
                                "location",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-md-2">
                          <input
                            className="form-control"
                            placeholder="Duration"
                            value={act.duration}
                            onChange={(e) =>
                              updateAction(
                                oIdx,
                                aIdx,
                                "duration",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-md-2">
                          <input
                            className="form-control"
                            placeholder="Frequency"
                            value={act.frequency}
                            onChange={(e) =>
                              updateAction(
                                oIdx,
                                aIdx,
                                "frequency",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-md-1 text-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeAction(oIdx, aIdx)}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-md mt-3"
                    onClick={() => addAction(oIdx)}
                  >
                    <i className="ri-add-line"></i> Add Action Step
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-outline-primary mt-3"
          onClick={addObjective}
        >
          <i className="ri-add-line"></i> Add Objective
        </button>
      </div>
    </div>
  );
};

export default IndividualisedRehabPlanForm;